import { TTLCache } from '@isaacs/ttlcache'
import { LocalStorage } from '@transsion/utils/persistent'

import { TokenType } from './enums'
import type { AuthenticationStorage, PermissionStorage } from './interfaces'
import type { ResourceItem, UserInfo } from './types'

export interface Options {
  prefixKey: string
}

export default class InternalStorage implements AuthenticationStorage, PermissionStorage {
  #prefixKey: string

  #cache: TTLCache<string, unknown[]>

  constructor(opts: Options) {
    if (!opts.prefixKey?.endsWith('_')) {
      throw new TypeError("storage's prefixKey is required and must be end with '_'")
    }

    this.#prefixKey = opts.prefixKey

    // 存储，最多存10个条目，5分钟过期
    this.#cache = new TTLCache({ max: 10, ttl: 5 * 60 * 1000 })
  }

  public getRtoken(): string | null {
    return LocalStorage.get(`${this.#prefixKey}${String(TokenType.REQUEST_TOKEN)}`, true)
  }

  public setRtoken(rtoken: string): void {
    LocalStorage.set(`${this.#prefixKey}${String(TokenType.REQUEST_TOKEN)}`, rtoken, true)
  }

  public getUtoken(): string | null {
    return LocalStorage.get(`${this.#prefixKey}${String(TokenType.USER_TOKEN)}`, true)
  }

  public setUtoken(utoken: string): void {
    LocalStorage.set(`${this.#prefixKey}${String(TokenType.USER_TOKEN)}`, utoken, true)
  }

  public setUserInfo(userInfo: UserInfo): void {
    LocalStorage.set(`${this.#prefixKey}user_info`, userInfo)
  }

  public getUserInfo(): UserInfo | null {
    return LocalStorage.get(`${this.#prefixKey}user_info`)
  }

  public setEmployeeNo(employeeNo: string): void {
    LocalStorage.set(`${this.#prefixKey}employee_no`, employeeNo, true)
  }

  public getEmployeeNo(): string | null {
    return LocalStorage.get(`${this.#prefixKey}employee_no`, true)
  }

  public setExpiresIn(expiresIn: number): void {
    LocalStorage.set(`${this.#prefixKey}expire_time`, Date.now() + expiresIn * 1000)
  }

  public getExpireTime(): number | null {
    return LocalStorage.get<number>(`${this.#prefixKey}expire_time`)
  }

  public getFunctionalPermissions(): ResourceItem[] | null {
    const key = `${this.#prefixKey}functional_permissions`

    return (this.#cache.get(key) as ResourceItem[]) ?? null
  }

  public setFunctionalPermissions(list: ResourceItem[]): void {
    this.#cache.set(`${this.#prefixKey}functional_permissions`, list)
  }

  public getResourceCodeList(): string[] | null {
    const key = `${this.#prefixKey}resource_code_list`

    return (this.#cache.get(key) as string[]) ?? null
  }

  public setResourceCodeList(list: string[]): void {
    this.#cache.set(`${this.#prefixKey}resource_code_list`, list)
  }

  public clear(): void {
    this.#cache.clear()

    const keys = [
      `${this.#prefixKey}${String(TokenType.REQUEST_TOKEN)}`,
      `${this.#prefixKey}${String(TokenType.USER_TOKEN)}`,
      `${this.#prefixKey}user_info`,
      `${this.#prefixKey}employee_no`,
      `${this.#prefixKey}expire_time`,
      `${this.#prefixKey}functional_permissions`,
      `${this.#prefixKey}resource_code_list`,
    ] as const

    for (const key of keys) {
      LocalStorage.remove(key)
    }
  }
}
